import React, { useState, useRef, useEffect } from "react";
import { Avatar } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import AppButton from "../layout/AppButton";

const CHAT_URL = `${process.env.REACT_APP_SERVER_URL}/aipd/chat/report`;

/**
 * Props:
 *   reportName  — display name of the current report
 *   pageName    — current page/tab name (for context)
 *   passtoken   — oidc-token
 *   getVisuals  — async function that returns [{title, type, csv}] from the embedded report
 */
export default function ReportChatBot({ reportName, pageName, passtoken, getVisuals }) {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([
    {
      role: "assistant",
      text: `Hi! I'm your data assistant for **${reportName || "this report"}**. Ask me anything about the data currently visible.`,
    },
  ]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const bottomRef                 = useRef(null);
  const panelRef                  = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const syncFullscreenState = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fullscreenElement === panelRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        syncFullscreenState
      );
    };
  }, []);

  const sendMessage = async () => {
    const q = input.trim();
    if (!q || loading) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);

    try {
      // Extract data from the embedded report visuals
      let visuals = [];
      if (typeof getVisuals === "function") {
        try {
          visuals = await getVisuals();
        } catch (e) {
          console.warn("Could not extract visual data:", e);
        }
      }

      const res = await axios.post(
        CHAT_URL,
        { question: q, reportName, pageName, visuals },
        { headers: { "oidc-token": passtoken } }
      );

      const answer = res.data?.answer || "Sorry, I couldn't get a response.";
      setMessages((m) => [...m, { role: "assistant", text: answer }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleFullscreen = async () => {
    if (!panelRef.current) return;

    try {
      if (!isFullscreen) {
        if (panelRef.current.requestFullscreen) {
          await panelRef.current.requestFullscreen();
        } else if (panelRef.current.webkitRequestFullscreen) {
          panelRef.current.webkitRequestFullscreen();
        }
        return;
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen toggle failed:", e);
    }
  };

  const closeChat = async () => {
    if (isFullscreen) {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      } catch (e) {
        console.warn("Could not exit fullscreen on close:", e);
      }
    }
    setOpen(false);
  };

  const chatbotPosition = {
    position: "fixed",
    right: 20,
    bottom: open ? 24 : 50,
    zIndex: 9999,
  };

  const assistantGradient =
    "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))";
  const assistantPrimary = "var(--app-ai-assistant-primary, #253978)";

  return (
    <div style={chatbotPosition}>
      {/* toggle button */}
      {!open && (
        <AppButton
          onClick={() => setOpen(true)}
          variant="fill"
          icon={<SmartToyIcon sx={{ fontSize: 18 }} />}
          className="shadow-xl hover:scale-105"
          style={{
            background: assistantGradient,
            boxShadow: "0 6px 24px rgba(37,57,120,0.45)",
            borderRadius: "9999px",
            padding: "10px 16px",
          }}
          title="Ask AI about this report"
        >
          Ask AI
        </AppButton>
      )}

      {/* chat panel */}
      {open && (
        <div
          ref={panelRef}
          className="bg-white rounded-2xl overflow-hidden"
          style={{
            width: isFullscreen ? "100vw" : 450,
            height: isFullscreen ? "100vh" : 720,
            borderRadius: isFullscreen ? 0 : undefined,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          }}
        >
          {/* header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: assistantGradient }}
          >
            <div className="flex items-center gap-2">
              <div
                className="flex items-center justify-center w-7 h-7 rounded-full"
                style={{ background: "rgba(255,255,255,0.15)" }}
              >
                <SmartToyIcon sx={{ fontSize: 16, color: "white" }} />
              </div>
              <div>
                <p className="text-white text-sm font-bold leading-tight">AI Data Assistant</p>
                <p className="text-white/60 text-[10px] leading-tight truncate max-w-[180px]">
                  {reportName || "Report"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleFullscreen}
                className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title={isFullscreen ? "Exit full screen" : "Full screen"}
              >
                {isFullscreen ? (
                  <FullscreenExitIcon sx={{ fontSize: 16 }} />
                ) : (
                  <FullscreenIcon sx={{ fontSize: 16 }} />
                )}
              </button>
              <button
                onClick={closeChat}
                className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Close"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex items-end gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                {msg.role === "assistant" && (
                  <Avatar
                    sx={{ width: 26, height: 26, bgcolor: assistantPrimary, fontSize: 11 }}
                  >
                    AI
                  </Avatar>
                )}
                <div
                  className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "text-white rounded-br-sm"
                      : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"
                  }`}
                  style={msg.role === "user" ? { background: assistantPrimary } : undefined}
                >
                  {msg.role === "assistant" ? (
                    <div className="markdownMessage">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => (
                            <p {...props} style={{ margin: "0 0 8px 0" }} />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul {...props} style={{ margin: "0 0 8px 18px" }} />
                          ),
                          ol: ({ node, ...props }) => (
                            <ol {...props} style={{ margin: "0 0 8px 18px" }} />
                          ),
                          pre: ({ node, ...props }) => (
                            <pre
                              {...props}
                              style={{
                                margin: "8px 0",
                                padding: "8px",
                                background: "#f3f4f6",
                                borderRadius: "8px",
                                overflowX: "auto",
                              }}
                            />
                          ),
                          code: ({ inline, node, ...props }) =>
                            inline ? (
                              <code
                                {...props}
                                style={{
                                  background: "#f3f4f6",
                                  borderRadius: "4px",
                                  padding: "1px 4px",
                                }}
                              />
                            ) : (
                              <code {...props} />
                            ),
                          a: ({ node, children, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {msg.text || ""}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-end gap-2">
                <Avatar sx={{ width: 26, height: 26, bgcolor: assistantPrimary, fontSize: 11 }}>
                  AI
                </Avatar>
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* input */}
          <div className="flex items-end gap-2 px-3 py-3 bg-white border-t border-gray-100 flex-shrink-0">
            <textarea
              rows={1}
              className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200"
              style={{ maxHeight: 80, overflowY: "auto" }}
              placeholder="Ask about the data…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
            />
            <AppButton
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              variant="fill"
              style={{
                background: assistantGradient,
                boxShadow: "0 4px 12px rgba(37,57,120,0.3)",
                borderRadius: "12px",
                width: 36,
                height: 36,
                padding: 0,
                justifyContent: "center",
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
}
